import { useState, useEffect } from "react";
import axios from "axios";
import { XIcon } from "@heroicons/react/outline";
import Button from "../misc/Button";
import LoadingSpinner from "../LoadingSpinner";

const GroupKeywordsManager = ({ groupId }) => {
  const [keywords, setKeywords] = useState([]);
  const [newKeywordTitle, setNewKeywordTitle] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (groupId) {
      fetchKeywords();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);

  const fetchKeywords = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get(`/api/group-keyword?GroupId=${groupId}`);
      setKeywords(response.data);
    } catch (err) {
      console.error("Error fetching keywords:", err);
      setError("Failed to load categories. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddKeyword = async () => {
    if (!newKeywordTitle.trim()) return;

    setIsAdding(true);
    setError(null);
    try {
      const response = await axios.post("/api/group-keyword", {
        title: newKeywordTitle.trim(),
        GroupId: groupId
      });
      setKeywords([...keywords, response.data]);
      setNewKeywordTitle("");
    } catch (err) {
      console.error("Error adding keyword:", err);
      setError("Failed to add category. Please try again.");
    } finally {
      setIsAdding(false);
    }
  };

  const handleDeleteKeyword = async keywordId => {
    const confirmed = window.confirm(
      "Deleting this category is irreversible. Contents will be removed from this category but will NOT be deleted. Do you want to proceed?"
    );

    if (!confirmed) return;

    setError(null);
    try {
      await axios.delete(`/api/keyword/${keywordId}`);
      setKeywords(keywords.filter(k => k.id !== keywordId));
    } catch (err) {
      console.error("Error deleting keyword:", err);
      setError("Failed to delete category. Please try again.");
    }
  };

  const handleKeyDown = e => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddKeyword();
    }
  };

  if (!groupId) {
    return (
      <div className="mt-2 p-4 bg-blue-50 border border-blue-200 rounded-md">
        <p className="text-sm text-blue-800">
          Save the group first to add categories. Categories will be available after creating the
          group.
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="mt-2 flex justify-center py-4">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="mt-2">
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Display existing keywords */}
      <div className="flex flex-wrap gap-2 mb-4">
        {keywords.length === 0 && !error && (
          <p className="text-sm text-gray-500">No categories yet. Add one below.</p>
        )}
        {keywords.map(keyword => (
          <div
            key={keyword.id}
            className="flex items-center px-3 py-1 bg-gray-100 border border-gray-300 rounded-md"
          >
            <span className="text-sm mr-2">{keyword.title}</span>
            <button
              type="button"
              onClick={() => handleDeleteKeyword(keyword.id)}
              className="text-red-600 hover:text-red-800"
              aria-label={`Delete ${keyword.title}`}
            >
              <XIcon className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>

      {/* Add new keyword */}
      <div className="flex gap-2">
        <input
          type="text"
          value={newKeywordTitle}
          onChange={e => setNewKeywordTitle(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Add new category..."
          className="flex-1 border-gray-300 focus:shadow-outline-blue focus:border-blue-300 px-3 py-2 border rounded-md focus:outline-none transition duration-150 ease-in-out sm:text-sm sm:leading-5"
          disabled={isAdding}
        />
        <Button onClick={handleAddKeyword} disabled={isAdding || !newKeywordTitle.trim()}>
          {isAdding ? "Adding..." : "Add"}
        </Button>
      </div>
    </div>
  );
};

export default GroupKeywordsManager;
